
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Lock, Sparkles, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BlurredConnectionsOverlayProps {
  isVisible: boolean;
}

const BlurredConnectionsOverlay: React.FC<BlurredConnectionsOverlayProps> = ({ isVisible }) => {
  const navigate = useNavigate();

  if (!isVisible) return null;

  const handleUpgradeClick = () => {
    navigate('/main/subscriptions');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="absolute top-0 left-0 right-0 bottom-0 z-30 flex items-center justify-center overflow-hidden"
    >
      {/* Progressive Blur Layers */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at center, 
              rgba(0,0,0,0.1) 0%,
              rgba(0,0,0,0.2) 30%,
              rgba(0,0,0,0.3) 60%,
              rgba(0,0,0,0.4) 100%
            )
          `,
          backdropFilter: 'blur(2px) saturate(150%)'
        }}
      />
      
      {/* Secondary Blur Layer - More Intense */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 70% 50% at center, 
              rgba(255,255,255,0.05) 0%,
              rgba(255,255,255,0.02) 40%,
              transparent 70%
            )
          `,
          backdropFilter: 'blur(6px) saturate(180%)',
          maskImage: 'radial-gradient(ellipse 80% 60% at center, black 0%, transparent 70%)'
        }}
      />

      {/* Glass Morphism Background */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            linear-gradient(135deg, 
              rgba(255,255,255,0.08) 0%,
              rgba(255,255,255,0.04) 25%,
              rgba(255,255,255,0.02) 50%,
              rgba(255,255,255,0.04) 75%,
              rgba(255,255,255,0.08) 100%
            )
          `,
          backdropFilter: 'blur(1px) brightness(1.05) contrast(1.1)',
        }}
      />

      {/* Subtle Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, rgba(255,255,255,0.05) 0%, transparent 50%),
            radial-gradient(circle at 60% 80%, rgba(255,255,255,0.05) 0%, transparent 50%)
          `,
          backgroundSize: '100% 100%, 100% 100%, 80% 80%, 80% 80%'
        }}
      />

      {/* Central Content Container */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ 
          delay: 0.3, 
          duration: 0.8, 
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "spring",
          stiffness: 100
        }}
        className="relative max-w-sm mx-4 p-8 pt-12 rounded-3xl overflow-visible"
        style={{
          background: `
            linear-gradient(135deg, 
              rgba(255,255,255,0.12) 0%,
              rgba(255,255,255,0.06) 50%,
              rgba(255,255,255,0.08) 100%
            )
          `,
          backdropFilter: 'blur(20px) saturate(200%)',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: `
            0 8px 32px rgba(0,0,0,0.3),
            0 2px 8px rgba(0,0,0,0.2),
            inset 0 1px 0 rgba(255,255,255,0.3),
            inset 0 -1px 0 rgba(255,255,255,0.1)
          `
        }}
      >
        {/* Inner Glass Reflection */}
        <div 
          className="absolute inset-0 rounded-3xl"
          style={{
            background: `
              linear-gradient(45deg, 
                rgba(255,255,255,0.1) 0%,
                transparent 30%,
                transparent 70%,
                rgba(255,255,255,0.05) 100%
              )
            `,
            maskImage: 'linear-gradient(45deg, black 0%, transparent 50%)'
          }}
        />

        {/* Premium Badge */}
        <motion.div 
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10"
        >
          <div 
            className="px-4 py-2 rounded-full text-xs font-bold flex items-center gap-2 backdrop-blur-sm whitespace-nowrap"
            style={{
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              color: '#000',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.4)'
            }}
          >
            <Crown className="h-3 w-3" />
            PREMIUM
          </div>
        </motion.div>

        {/* Content */}
        <div className="text-center space-y-6 mt-2 relative z-10">
          {/* Icon Container */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="relative">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center backdrop-blur-sm"
                style={{
                  background: `
                    radial-gradient(circle, 
                      rgba(251, 191, 36, 0.2) 0%,
                      rgba(245, 158, 11, 0.1) 100%
                    )
                  `,
                  border: '2px solid rgba(251, 191, 36, 0.3)',
                  boxShadow: '0 8px 20px rgba(251, 191, 36, 0.2)'
                }}
              >
                <Eye className="h-9 w-9 text-yellow-400" />
              </div>
              <motion.div 
                animate={{ 
                  rotate: [0, 360],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute -top-2 -right-2"
              >
                <Sparkles className="h-6 w-6 text-yellow-400" />
              </motion.div>
            </div>
          </motion.div>

          {/* Title and Description */}
          <motion.div 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="space-y-3"
          >
            <h3 className="text-2xl font-bold text-white">
              Visualizações Premium
            </h3>
            <p className="text-white/90 text-base leading-relaxed">
              Veja quem visualizou seu perfil
            </p>
          </motion.div>

          {/* Features List */}
          <motion.div 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="space-y-3 text-white/80"
          >
            <p className="text-sm leading-relaxed">
              Esta funcionalidade está disponível nos planos <strong className="text-yellow-400">Standard</strong> e <strong className="text-yellow-400">Premium</strong>.
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-3 text-sm">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                <span>Veja quem visitou seu perfil</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-sm">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                <span>Histórico completo</span>
              </div>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <Button
              onClick={handleUpgradeClick}
              className="w-full font-bold py-3 px-6 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                color: '#000',
                boxShadow: '0 6px 16px rgba(251, 191, 36, 0.4)'
              }}
            >
              <Crown className="h-4 w-4 mr-2" />
              Fazer Upgrade
            </Button>
          </motion.div>

          {/* Bottom Text */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="text-xs text-white/60 mt-4"
          >
            Descubra quem tem interesse em você
          </motion.p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BlurredConnectionsOverlay;
